import { $http } from '@/utils/https';
// @ts-ignore
import { readGiftSelection, writeGiftSelection, clearGiftSelection, buildBasketHash } from '@/helpers/gift';

export interface GiftVariant {
  productPriceId: number;
  maxQuantity: number | null;
  givenQuantity: number;
  product: {
    id: number;
    slug: string;
    title: string;
    image: string;
    productPrices: { data: Array<{ id: number; title: string; value?: any }> };
  };
}

export interface GiftGroup {
  id: number;
  maxCount: number;
  products: GiftVariant[];
}

export interface SelectedGift {
  giftGroupId: number;
  productId: number;
  productPriceId: number;
  count: number;
}

interface State {
  isGiftsLoading: boolean;
  giftGroups: GiftGroup[];
  selectedGifts: SelectedGift[];
}

const state: State = {
  isGiftsLoading: false,
  giftGroups: [],
  selectedGifts: []
};

const getters = {
  isGiftsLoading: (state: State) => state.isGiftsLoading,
  giftGroups: (state: State) => state.giftGroups,
  selectedGifts: (state: State) => state.selectedGifts,
  earnedGiftCount: (state: State) => state.giftGroups.reduce((sum, group) => sum + (group.maxCount || 0), 0),
  selectedGiftCount: (state: State) => state.selectedGifts.reduce((sum, item) => sum + (item.count || 0), 0),
  hasUnselectedGifts: (state: State) => {
    const earned = state.giftGroups.reduce((sum, group) => sum + (group.maxCount || 0), 0);
    if (earned === 0) return false;
    const selected = state.selectedGifts.reduce((sum, item) => sum + (item.count || 0), 0);
    return selected < earned;
  },
  gifts: () => null
};

const mapApiOfferToGroup = (raw: any): GiftGroup | null => {
  if (!raw) return null;
  const giftId = raw.giftId ?? raw.gift_id ?? raw.id;
  const maxCount = raw.maxCount ?? raw.max_count ?? 0;
  if (giftId == null) return null;
  const products = Array.isArray(raw.products) ? raw.products : [];
  return {
    id: Number(giftId),
    maxCount: Number(maxCount) || 0,
    products: products.map((pp: any) => ({
      productPriceId: pp.id,
      maxQuantity: pp.maxQuantity ?? pp.max_quantity ?? null,
      givenQuantity: pp.givenQuantity ?? pp.given_quantity ?? 0,
      product: {
        id: pp.productId,
        slug: pp.product?.slug ?? pp.productSlug ?? pp.product_slug ?? '',
        title: pp.product?.title ?? pp.productTitle ?? pp.title,
        image: pp.product?.image ?? pp.image ?? '',
        productPrices: {
          data: [
            {
              id: pp.id,
              title: pp.title,
              value: pp.value
            }
          ]
        }
      }
    }))
  };
};

const actions = {
  CHECK_GIFTS: async ({ state, commit, rootGetters }: any, payload: any) => {
    try {
      commit('CHANGE_GIFT_LOADING', true);
      const response = await $http.get<{ data: any }>('v1/products/check-gift', {
        params: payload
      });
      const body: any = response.data;
      const raw = Array.isArray(body) ? body : body?.data;
      const groups: GiftGroup[] = Array.isArray(raw)
        ? (raw.map(mapApiOfferToGroup).filter(Boolean) as GiftGroup[])
        : [];
      commit('SET_GIFT_GROUPS', groups);

      const basket = rootGetters['basket/basket'];
      const basketHash = buildBasketHash(basket);

      let candidate: SelectedGift[] = [];
      if (Array.isArray(state.selectedGifts) && state.selectedGifts.length > 0) {
        candidate = state.selectedGifts;
      } else {
        const stored = readGiftSelection();
        if (stored && Array.isArray(stored.selectedGifts)) {
          candidate = stored.selectedGifts as SelectedGift[];
        }
      }

      const perGroupCount: Record<number, number> = {};
      const selected: SelectedGift[] = [];
      for (const item of candidate) {
        const group = groups.find((g) => g.id === item.giftGroupId);
        if (!group) continue;
        if (!group.products.some((v) => v.productPriceId === item.productPriceId)) continue;
        const used = perGroupCount[item.giftGroupId] || 0;
        const remaining = group.maxCount - used;
        if (remaining <= 0) continue;
        const count = Math.min(item.count, remaining);
        selected.push({ ...item, count });
        perGroupCount[item.giftGroupId] = used + count;
      }

      if (selected.length > 0) {
        writeGiftSelection(basketHash, selected);
      } else {
        clearGiftSelection();
      }
      commit('SET_SELECTED_GIFTS', selected);
      return groups;
    } catch (e) {
      throw e;
    } finally {
      commit('CHANGE_GIFT_LOADING', false);
    }
  },

  APPLY_SELECTED_GIFTS: ({ commit, rootGetters }: any, selected: SelectedGift[]) => {
    const basket = rootGetters['basket/basket'];
    const basketHash = buildBasketHash(basket);
    writeGiftSelection(basketHash, selected);
    commit('SET_SELECTED_GIFTS', selected);
  },

  REMOVE_SELECTED_GIFT: (
    { state, commit, rootGetters }: any,
    payload: { giftGroupId: number; productPriceId: number }
  ) => {
    const next = (state.selectedGifts || []).filter(
      (item: SelectedGift) =>
        !(item.giftGroupId === payload.giftGroupId && item.productPriceId === payload.productPriceId)
    );
    const basket = rootGetters['basket/basket'];
    const basketHash = buildBasketHash(basket);
    if (next.length > 0) {
      writeGiftSelection(basketHash, next);
    } else {
      clearGiftSelection();
    }
    commit('SET_SELECTED_GIFTS', next);
  },

  RESET_SELECTED_GIFTS: ({ commit }: any) => {
    clearGiftSelection();
    commit('SET_SELECTED_GIFTS', []);
    commit('SET_GIFT_GROUPS', []);
  }
};

const mutations = {
  CHANGE_GIFT_LOADING(state: State, status: boolean) {
    state.isGiftsLoading = status;
  },
  SET_GIFT_GROUPS(state: State, groups: GiftGroup[]) {
    state.giftGroups = groups || [];
  },
  SET_SELECTED_GIFTS(state: State, selected: SelectedGift[]) {
    state.selectedGifts = selected || [];
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
